import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import { useNavigate } from 'react-router-dom';
import { 
  FaMapPin, FaClock, FaExclamationTriangle, FaUserCheck, FaCheckCircle, 
  FaSearch, FaFilter, FaHandPaper, FaSpinner, FaInfoCircle,
  FaBolt, FaFire, FaWater, FaMountain
} from 'react-icons/fa';
import Preloader from './Preloader';

function AvailableIncidents() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [responderProfile, setResponderProfile] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [myResponses, setMyResponses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [responding, setResponding] = useState(null);
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Available Incidents";
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser && responderProfile) {
      fetchAvailableIncidents();
      fetchMyResponses();
      
      // Set up real-time subscriptions
      setupRealtimeSubscriptions();
    }
  }, [currentUser, responderProfile]);

  async function getCurrentUser() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setCurrentUser(profile);

      // Check if user is an approved responder
      const { data: responder, error: responderError } = await supabase
        .from('approved_responders')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (responderError) {
        console.error('Error fetching responder profile:', responderError);
        setError('You must be an approved responder to access this page.');
        return;
      }

      setResponderProfile(responder);
    } catch (error) {
      console.error('Error getting current user:', error);
      setError('Failed to load user information');
    } finally {
      setLoading(false);
    }
  }

  function setupRealtimeSubscriptions() {
    // Subscribe to incident reports changes
    const incidentChannel = supabase
      .channel('available_incidents')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'incident_reports' },
        (payload) => {
          console.log('Incident change:', payload);
          fetchAvailableIncidents();
        }
      )
      .subscribe();

    // Subscribe to responder responses changes
    const responseChannel = supabase
      .channel('responder_responses')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'responder_incident_responses' },
        (payload) => {
          console.log('Response change:', payload);
          fetchMyResponses();
          fetchAvailableIncidents();
        }
      )
      .subscribe();

    return () => {
      incidentChannel.unsubscribe();
      responseChannel.unsubscribe();
    };
  }

  async function fetchAvailableIncidents() {
    try {
      // Fetch active incidents that don't have too many responders yet
      const { data, error } = await supabase
        .from('incident_reports')
        .select(`
          *,
          responder_incident_responses!left(
            id,
            status,
            responder_id
          )
        `)
        .in('status', ['active', 'responding'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter out incidents that already have enough responders or user already responded
      const availableIncidents = data?.filter(incident => {
        const responses = incident.responder_incident_responses || [];
        const activeResponses = responses.filter(r => 
          ['accepted', 'responding', 'on_scene'].includes(r.status)
        );
        
        // Check if current responder already responded
        const alreadyResponded = responses.some(r => 
          r.responder_id === responderProfile?.id && 
          ['accepted', 'responding', 'on_scene'].includes(r.status)
        );

        // Allow incidents with less than 5 active responders and user hasn't responded
        return activeResponses.length < 5 && !alreadyResponded;
      }) || [];

      setIncidents(availableIncidents);
    } catch (error) {
      console.error('Error fetching available incidents:', error);
      setError('Failed to load available incidents');
    }
  }

  async function fetchMyResponses() {
    try {
      if (!responderProfile?.id) return;

      const { data, error } = await supabase
        .from('responder_incident_responses')
        .select(`
          *,
          incident_reports!inner(
            id,
            type,
            location,
            severity,
            status,
            created_at
          )
        `)
        .eq('responder_id', responderProfile.id)
        .in('status', ['accepted', 'responding', 'on_scene'])
        .order('accepted_at', { ascending: false });

      if (error) throw error;
      setMyResponses(data || []);
    } catch (error) {
      console.error('Error fetching my responses:', error);
    }
  }

  async function respondToIncident(incident) {
    if (!responderProfile) {
      setError('Responder profile not found');
      return;
    }

    setResponding(incident.id);
    setError('');
    
    try {
      // Check if already responded
      const { data: existingResponse, error: checkError } = await supabase
        .from('responder_incident_responses')
        .select('id')
        .eq('responder_id', responderProfile.id)
        .eq('incident_id', incident.id)
        .in('status', ['accepted', 'responding', 'on_scene'])
        .single();

      if (existingResponse) {
        setError('You have already responded to this incident');
        return;
      }

      // Create incident response record
      const { error: responseError } = await supabase
        .from('responder_incident_responses')
        .insert([{
          responder_id: responderProfile.id,
          incident_id: incident.id,
          accepted_at: new Date().toISOString(),
          status: 'accepted',
          assignment_type: 'voluntary'
        }]);

      if (responseError) throw responseError;

      // Update responder availability if the function exists
      try {
        await supabase.rpc('update_responder_availability', {
          responder_bigint: responderProfile.id,
          new_status: 'responding',
          incident_id: incident.id
        });
      } catch (funcError) {
        console.log('Availability function not available:', funcError);
      }

      // Log the response in audit logs
      await supabase
        .from('audit_logs')
        .insert([{
          user_id: currentUser.user_id,
          action: 'responded to incident',
          target_id: incident.id,
          target_type: 'IncidentReports',
          details: {
            incident_location: incident.location,
            incident_type: incident.type,
            responder_name: `${responderProfile.first_name} ${responderProfile.last_name}`,
            responder_id: responderProfile.responder_id,
            response_type: 'voluntary'
          }
        }]);

      setSuccess(`Successfully responded to ${incident.type} incident at ${incident.location}`);
      
      // Refresh data
      await fetchAvailableIncidents();
      await fetchMyResponses();

    } catch (error) {
      console.error('Error responding to incident:', error);
      setError('Failed to respond to incident. Please try again.');
    } finally {
      setResponding(null);
    }
  }

  function getIncidentIcon(type) {
    switch (type?.toLowerCase()) {
      case 'fire': return <FaFire style={{ color: '#ef4444' }} />;
      case 'earthquake': return <FaMountain style={{ color: '#8b5cf6' }} />;
      case 'tsunami': return <FaWater style={{ color: '#06b6d4' }} />;
      case 'eruption': return <FaBolt style={{ color: '#f59e0b' }} />;
      default: return <FaExclamationTriangle style={{ color: '#f97316' }} />;
    }
  }

  function getSeverityColor(severity) {
    switch (severity?.toLowerCase()) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  }

  function formatTimeAgo(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  }

  const filteredIncidents = useMemo(() => {
    return incidents.filter(incident => {
      const matchesSearch = !searchTerm || 
        incident.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSeverity = severityFilter === 'all' || incident.severity === severityFilter;
      const matchesType = typeFilter === 'all' || incident.type === typeFilter;
      
      return matchesSearch && matchesSeverity && matchesType;
    });
  }, [incidents, searchTerm, severityFilter, typeFilter]);

  if (loading) return <Preloader text="Loading available incidents..." />;

  if (!responderProfile) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, padding: '32px 48px', fontWeight: 500, fontSize: 20 }}>
            <FaInfoCircle style={{ marginRight: 8 }} />
            You must be an approved responder to access this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: 32, maxWidth: 'calc(100vw - 280px)', overflowX: 'auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 'bold', margin: 0, color: '#2c3e50' }}>
              Available Incidents
            </h1>
            <p style={{ color: '#7f8c8d', margin: '8px 0 0 0' }}>
              Respond to incidents in your area and help save lives
            </p>
          </div>
          <ProfileMenu />
        </div>

        {/* Alert Messages */}
        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            {error}
          </div>
        )}
        
        {success && (
          <div style={{ background: '#e8f5e8', color: '#2e7d32', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            <FaCheckCircle style={{ marginRight: 8 }} />
            {success}
          </div>
        )}

        {/* My Current Responses */}
        {myResponses.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaUserCheck style={{ color: '#10b981' }} />
              My Active Responses ({myResponses.length})
            </h2>
            <div style={{ display: 'grid', gap: 12 }}>
              {myResponses.map(response => (
                <div key={response.id} style={{
                  background: 'linear-gradient(145deg, #ecfdf5, #d1fae5)',
                  border: '1px solid #86efac',
                  borderRadius: 8,
                  padding: 16,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {getIncidentIcon(response.incident_reports.type)}
                    <div>
                      <div style={{ fontWeight: 600, color: '#1f2937' }}>
                        {response.incident_reports.type} - {response.incident_reports.location}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        Responded {formatTimeAgo(response.accepted_at)} • Status: {response.status}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    background: '#10b981',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'capitalize'
                  }}>
                    {response.status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 2 }}>
            <FaSearch style={{ 
              position: 'absolute', 
              left: 12, 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#9ca3af' 
            }} />
            <input
              type="text"
              placeholder="Search incidents by type, location, or description..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '12px 12px 12px 40px', 
                borderRadius: 8, 
                border: '1px solid #d1d5db',
                fontSize: 14
              }}
            />
          </div>
          
          <select
            value={severityFilter}
            onChange={e => setSeverityFilter(e.target.value)}
            style={{ 
              flex: 1, 
              minWidth: 120, 
              padding: 12, 
              borderRadius: 8, 
              border: '1px solid #d1d5db',
              fontSize: 14
            }}
          >
            <option value="all">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{ 
              flex: 1, 
              minWidth: 120, 
              padding: 12, 
              borderRadius: 8, 
              border: '1px solid #d1d5db',
              fontSize: 14
            }}
          >
            <option value="all">All Types</option>
            <option value="fire">Fire</option>
            <option value="earthquake">Earthquake</option>
            <option value="tsunami">Tsunami</option>
            <option value="eruption">Eruption</option>
          </select>
        </div>

        {/* Incidents List */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', margin: '0 0 20px 0' }}>
            Available Incidents ({filteredIncidents.length})
          </h2>

          {filteredIncidents.length === 0 ? (
            <div style={{ 
              textAlign: 'center',
              color: '#6b7280',
              padding: '60px 20px',
              background: '#f9fafb',
              borderRadius: 8
            }}>
              <FaHandPaper style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Available Incidents</h3>
              <p style={{ margin: 0, fontSize: 14 }}>
                {searchTerm || severityFilter !== 'all' || typeFilter !== 'all' 
                  ? 'No incidents match your current filters.'
                  : 'No incidents currently need responders.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {filteredIncidents.map(incident => {
                const activeResponders = incident.responder_incident_responses?.filter(r => 
                  ['accepted', 'responding', 'on_scene'].includes(r.status)
                ).length || 0;

                return (
                  <div key={incident.id} style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: 20,
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {getIncidentIcon(incident.type)}
                        <div>
                          <h3 style={{ margin: '0 0 4px 0', fontSize: 18, fontWeight: 600, color: '#1f2937' }}>
                            {incident.type} Emergency
                          </h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#6b7280' }}>
                            <FaMapPin />
                            {incident.location}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          background: getSeverityColor(incident.severity),
                          color: '#fff',
                          padding: '4px 12px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          marginBottom: 4
                        }}>
                          {incident.severity}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          <FaClock style={{ marginRight: 4 }} />
                          {formatTimeAgo(incident.created_at)}
                        </div>
                      </div>
                    </div>

                    {incident.description && (
                      <p style={{ color: '#374151', margin: '0 0 16px 0', fontSize: 14 }}>
                        {incident.description}
                      </p>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: '#6b7280' }}>
                        <div>
                          Status: <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{incident.status}</span>
                        </div>
                        <div>
                          Responders: <span style={{ fontWeight: 500 }}>{activeResponders}/5</span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => respondToIncident(incident)}
                        disabled={responding === incident.id}
                        style={{
                          background: responding === incident.id ? '#9ca3af' : '#10b981',
                          color: '#fff',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: 8,
                          cursor: responding === incident.id ? 'not-allowed' : 'pointer',
                          fontSize: 14,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          opacity: responding === incident.id ? 0.7 : 1
                        }}
                      >
                        {responding === incident.id ? (
                          <>
                            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                            Responding...
                          </>
                        ) : (
                          <>
                            <FaHandPaper />
                            Respond
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AvailableIncidents;